import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Gift, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { BirthDatePicker, BirthTimePicker } from "@/components/BirthDateTime";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";

const input = "w-full bg-[#04060B] border border-[#D4AF37]/20 rounded-xl px-3 py-2.5 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60";

function SignupFields({ f, set }) {
  return (
    <>
      <input data-testid="gift-claim-name" className={input} placeholder="Your first name" value={f.name} onChange={(e) => set("name")(e.target.value)} />
      <div><label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Date of birth</label><BirthDatePicker testId="gift-claim-dob" value={f.birth_date} onChange={set("birth_date")} /></div>
      <div><label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Time of birth (optional, makes it more accurate)</label><BirthTimePicker testId="gift-claim-tob" value={f.birth_time} onChange={set("birth_time")} /></div>
      <input data-testid="gift-claim-pob" className={input} placeholder="City of birth" value={f.birth_place} onChange={(e) => set("birth_place")(e.target.value)} />
      <input data-testid="gift-claim-email" type="email" className={input} placeholder="Email" value={f.email} onChange={(e) => set("email")(e.target.value)} />
      <input data-testid="gift-claim-password" type="password" className={input} placeholder="Choose a password (6+ characters)" value={f.password} onChange={(e) => set("password")(e.target.value)} />
    </>
  );
}

export default function GiftClaim() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ name: "", birth_date: "", birth_time: "", birth_place: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/public/gift/${token}`).then(({ data }) => {
      setInfo(data);
      setF((s) => ({ ...s, name: data.recipient_name, email: data.recipient_email }));
      if (data.ref) localStorage.setItem("astra_ref", data.ref);
    }).catch((e) => setError(e.response?.data?.detail || "This gift link isn't valid"));
  }, [token]);

  const open = async () => {
    setBusy(true);
    try {
      if (!user) await register({ ...f, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      await api.post(`/gifts/${token}/claim`);
      toast.success(`Your ${info.title} is in your Archive`);
      navigate("/history");
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong. Please try again."); setBusy(false); }
  };
  const ready = user || (f.name && f.birth_date && f.email && f.password.length >= 6);

  return (
    <PublicShell testId="gift-claim-page">
      <div className={`px-5 pt-6 fade-up ${WIDE ? "lg:max-w-xl lg:mx-auto lg:pt-14" : ""}`}>
        {error ? <p data-testid="gift-claim-error" className="text-center text-[#A7B0C0] py-20">{error}</p> : !info ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin gold-text" size={28} /></div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Gift size={11} className="inline -mt-0.5 mr-1" />A gift for you</p>
            <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-3" data-testid="gift-claim-title">{info.from_name} sent you {/^[AEIOU]/i.test(info.title) ? "an" : "a"} {info.title}</h1>
            {info.message && <p className="text-lg italic text-[#E8DCC4] mb-3" data-testid="gift-claim-message">“{info.message}”</p>}
            <p className="text-base text-[#A7B0C0] mb-7">Add your birth details so it's written from where the planets were on the day you were born. It's yours to keep.</p>
            <div className="bg-[#0F1523]/90 border border-[#D4AF37]/25 rounded-3xl p-6 space-y-2.5" data-testid="gift-claim-form">
              {!user && <SignupFields f={f} set={set} />}
              {user && <p className="text-base text-[#D6D0C5]">Signed in as {user.name}. Your saved birth details will be used.</p>}
              <p className="text-xs text-[#A7B0C0] flex gap-2 pt-1"><ShieldCheck size={14} className="gold-text shrink-0" />Your birth details are used only to calculate your chart and stay private. {info.from_name} never sees them.</p>
              <button data-testid="btn-gift-claim" onClick={open} disabled={busy || !ready || info.claimed}
                className="w-full mt-3 flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
                {busy ? <><Loader2 className="animate-spin" size={18} /> Writing your reading…</> : info.claimed ? "This gift has been opened" : user ? "Open my gift" : "Create my free account and open my gift"}
              </button>
              {!user && !info.claimed && <p className="text-sm text-[#A7B0C0] pt-2">Already have an account? <Link to={`/join?mode=login&next=${encodeURIComponent(`/gift/${token}`)}`} className="gold-text" data-testid="gift-claim-signin">Sign in</Link></p>}
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
